import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, X } from 'lucide-react';

interface EvaluationCategory {
  name: string;
  weightPercent: number;
  minimumPassingScore?: number | null;
}

interface CurriculumCourse {
  id: number;
  code: string;
  name: string;
  description?: string;
  credits: number;
  totalSessions?: number;
  hoursPerSession?: number;
  evaluationPlanCategories?: string;
  createdAt: string;
  updatedAt: string;
}

interface Equipment {
  id: number;
  name: string;
  description?: string;
  capacity?: number;
  isActive: boolean;
}

export default function Materias() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<CurriculumCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<CurriculumCourse | null>(null);
  const [saving, setSaving] = useState(false);
  const [availableEquipments, setAvailableEquipments] = useState<Equipment[]>([]);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    credits: 0,
    totalSessions: '',
    hoursPerSession: ''
  });
  const [evaluationCategories, setEvaluationCategories] = useState<EvaluationCategory[]>([]);
  const [newCategory, setNewCategory] = useState({ name: '', weightPercent: '', minimumPassingScore: '' });

  useEffect(() => {
    loadCourses();
    loadEquipments();
  }, []);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch('/api/curriculum-courses', {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Error al cargar materias');
      const data = await response.json();
      const sorted = Array.isArray(data) ? data.sort((a: CurriculumCourse, b: CurriculumCourse) => a.code.localeCompare(b.code, 'es')) : [];
      setCourses(sorted);
    } catch (err) {
      console.error('Error cargando materias:', err);
      setError('Error al cargar las materias');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEquipments = async () => {
    try {
      const response = await fetch('/api/equipments', {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Error al cargar equipamientos');
      const data = await response.json();
      setAvailableEquipments(Array.isArray(data) ? data.filter((e: Equipment) => e.isActive) : []);
    } catch (err) {
      console.error('Error cargando equipamientos:', err);
    }
  };

  const loadCourseEquipments = async (courseId: number) => {
    try {
      const response = await fetch(`/api/curriculum-courses/${courseId}/equipments`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Error al cargar equipamientos de la materia');
      const data = await response.json();
      const equipmentIds = data.map((e: any) => e.equipmentId);
      setSelectedEquipmentIds(equipmentIds);
    } catch (err) {
      console.error('Error cargando equipamientos de la materia:', err);
      setSelectedEquipmentIds([]);
    }
  };

  const handleOpenModal = async (course?: CurriculumCourse) => {
    if (course) {
      setEditingCourse(course);
      setFormData({
        code: course.code,
        name: course.name,
        description: course.description || '',
        credits: course.credits,
        totalSessions: course.totalSessions?.toString() || '',
        hoursPerSession: course.hoursPerSession?.toString() || ''
      });
      await loadCourseEquipments(course.id);
      let parsedCategories: EvaluationCategory[] = [];
      if (course.evaluationPlanCategories) {
        try {
          parsedCategories = JSON.parse(course.evaluationPlanCategories);
        } catch (e) {
          console.error('Error parsing evaluation categories:', e);
        }
      }
      setEvaluationCategories(parsedCategories);
    } else {
      setEditingCourse(null);
      setFormData({ code: '', name: '', description: '', credits: 0, totalSessions: '', hoursPerSession: '' });
      setSelectedEquipmentIds([]);
      setEvaluationCategories([]);
    }
    setNewCategory({ name: '', weightPercent: '', minimumPassingScore: '' });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCourse(null);
    setFormData({ code: '', name: '', description: '', credits: 0, totalSessions: '', hoursPerSession: '' });
    setSelectedEquipmentIds([]);
    setEvaluationCategories([]);
    setNewCategory({ name: '', weightPercent: '', minimumPassingScore: '' });
  };

  const handleAddCategory = () => {
    if (!newCategory.name.trim() || !newCategory.weightPercent) return;
    const weight = parseFloat(newCategory.weightPercent);
    if (isNaN(weight) || weight <= 0 || weight > 100) {
      alert('El peso debe ser un número entre 0 y 100');
      return;
    }
    const minScore = newCategory.minimumPassingScore ? parseFloat(newCategory.minimumPassingScore) : null;
    setEvaluationCategories([...evaluationCategories, { name: newCategory.name.trim(), weightPercent: weight, minimumPassingScore: minScore }]);
    setNewCategory({ name: '', weightPercent: '', minimumPassingScore: '' });
  };

  const handleRemoveCategory = (index: number) => {
    setEvaluationCategories(evaluationCategories.filter((_, i) => i !== index));
  };

  const getTotalWeight = () => {
    return evaluationCategories.reduce((sum, cat) => sum + cat.weightPercent, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.code.trim() || !formData.name.trim()) {
      alert('El código y nombre son requeridos');
      return;
    }

    try {
      setSaving(true);
      const url = editingCourse 
        ? `/api/curriculum-courses/${editingCourse.id}` 
        : '/api/curriculum-courses';
      const method = editingCourse ? 'PUT' : 'POST';
      
      const payload: any = {
        code: formData.code,
        name: formData.name,
        description: formData.description,
        credits: formData.credits,
        evaluationPlanCategories: evaluationCategories.length > 0 ? evaluationCategories : null
      };
      
      if (formData.totalSessions) {
        payload.totalSessions = parseInt(formData.totalSessions);
      }
      if (formData.hoursPerSession) {
        payload.hoursPerSession = parseFloat(formData.hoursPerSession);
      }
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error('Error al guardar');
      const savedCourse = await response.json();
      
      const courseId = editingCourse ? editingCourse.id : savedCourse.id;
      await fetch(`/api/curriculum-courses/${courseId}/equipments`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ equipmentIds: selectedEquipmentIds })
      });

      await loadCourses();
      handleCloseModal();
    } catch (err) {
      console.error('Error guardando materia:', err);
      alert('Error al guardar la materia');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (course: CurriculumCourse) => {
    if (!confirm(`¿Eliminar la materia "${course.name}"?`)) return;
    
    try {
      const response = await fetch(`/api/curriculum-courses/${course.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Error al eliminar');
      await loadCourses();
    } catch (err) {
      console.error('Error eliminando materia:', err);
      alert('Error al eliminar la materia');
    }
  };

  const filteredCourses = courses.filter((course) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      course.code.toLowerCase().includes(searchLower) ||
      course.name.toLowerCase().includes(searchLower) ||
      course.description?.toLowerCase().includes(searchLower) ||
      course.credits.toString().includes(searchLower) ||
      course.totalSessions?.toString().includes(searchLower) ||
      course.hoursPerSession?.toString().includes(searchLower)
    );
  });

  return (
    <div className="flex flex-col h-screen p-6 overflow-hidden">
      <button
        onClick={() => navigate('/app/catalogos')}
        className="mb-4 flex items-center text-gray-600 hover:text-gray-900 font-medium"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Volver a Catálogos
      </button>

      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catálogo de Materias</h1>
          <p className="text-gray-600">Gestión de asignaturas y cursos académicos</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
        >
          + Nueva Materia
        </button>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {courses.length > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por código, nombre, créditos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
          {searchTerm && (
            <span className="text-sm text-gray-500">
              {filteredCourses.length} de {courses.length} resultados
            </span>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto pr-4">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Cargando materias...</div>
        ) : courses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No hay materias registradas</p>
            <p className="text-gray-400 text-sm mt-2">Comienza agregando tu primera materia</p>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No se encontraron resultados</p>
            <p className="text-gray-400 text-sm mt-2">Intenta con otros términos de búsqueda</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="w-full">
              <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Créditos</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">N° Clases</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duración (hrs)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCourses.map((course) => (
                <tr key={course.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{course.code}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{course.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{course.credits}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{course.totalSessions || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{course.hoursPerSession || '-'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600">{course.description || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => handleOpenModal(course)} className="text-emerald-600 hover:text-emerald-900 mr-4">
                      Editar
                    </button>
                    <button onClick={() => handleDelete(course)} className="text-red-600 hover:text-red-900">
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-bold">{editingCourse ? 'Editar Materia' : 'Nueva Materia'}</h2>
            </div>
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Ej: MAT-101"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Ej: Matemáticas I"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Créditos</label>
                  <input
                    type="number"
                    value={formData.credits}
                    onChange={(e) => setFormData({ ...formData, credits: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    min="0"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número de Clases</label>
                    <input
                      type="number"
                      value={formData.totalSessions}
                      onChange={(e) => setFormData({ ...formData, totalSessions: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      min="1"
                      placeholder="Ej: 14"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Horas por Clase</label>
                    <input
                      type="number"
                      step="0.5"
                      value={formData.hoursPerSession}
                      onChange={(e) => setFormData({ ...formData, hoursPerSession: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      min="0.5"
                      placeholder="Ej: 3.5"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    rows={3}
                    placeholder="Descripción de la materia"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Equipamientos Requeridos</label>
                  <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-gray-50">
                    {availableEquipments.length === 0 ? (
                      <p className="text-sm text-gray-500">No hay equipamientos disponibles</p>
                    ) : (
                      <div className="space-y-2">
                        {availableEquipments.map((equipment) => (
                          <label key={equipment.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 p-2 rounded">
                            <input
                              type="checkbox"
                              checked={selectedEquipmentIds.includes(equipment.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedEquipmentIds([...selectedEquipmentIds, equipment.id]);
                                } else {
                                  setSelectedEquipmentIds(selectedEquipmentIds.filter(id => id !== equipment.id));
                                }
                              }}
                              className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">
                              {equipment.name}
                              {equipment.capacity && <span className="text-gray-500 ml-1">(Cap: {equipment.capacity})</span>}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Selecciona los equipamientos necesarios para impartir esta materia</p>
                </div>

                <div className="border-t pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Plan de Evaluación</label>
                  <p className="text-xs text-gray-500 mb-3">Define las categorías de evaluación y sus pesos (deben sumar 100%)</p>
                  
                  {evaluationCategories.length > 0 && (
                    <div className="mb-3 space-y-2">
                      {evaluationCategories.map((cat, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                          <span className="text-sm text-gray-700 flex-1">{cat.name}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-emerald-600">{cat.weightPercent}%</span>
                            {cat.minimumPassingScore != null && (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded font-medium">
                                Mín ≥{cat.minimumPassingScore}
                              </span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveCategory(index)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      <div className={`text-sm font-medium px-3 py-1 rounded ${getTotalWeight() === 100 ? 'text-green-600 bg-green-50' : 'text-orange-600 bg-orange-50'}`}>
                        Total: {getTotalWeight()}%
                        {getTotalWeight() !== 100 && <span className="ml-2 text-xs">(debe ser 100%)</span>}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2 flex-wrap">
                    <input
                      type="text"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      placeholder="Ej: Parcial 1"
                      className="flex-1 min-w-[120px] px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <input
                      type="number"
                      value={newCategory.weightPercent}
                      onChange={(e) => setNewCategory({ ...newCategory, weightPercent: e.target.value })}
                      placeholder="Peso %"
                      min="1"
                      max="100"
                      className="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <input
                      type="number"
                      value={newCategory.minimumPassingScore}
                      onChange={(e) => setNewCategory({ ...newCategory, minimumPassingScore: e.target.value })}
                      placeholder="Mín ≥"
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-20 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      title="Calificación mínima aprobatoria (opcional)"
                    />
                    <button
                      type="button"
                      onClick={handleAddCategory}
                      className="px-3 py-2 bg-emerald-100 text-emerald-700 rounded-md hover:bg-emerald-200 text-sm font-medium"
                    >
                      +
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Mín ≥: calificación mínima aprobatoria en esa categoría (opcional)</p>
                </div>
              </div>
            </form>
            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end flex-shrink-0">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
                disabled={saving}
              >
                {saving ? 'Guardando...' : editingCourse ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
